"use client";

import { useState, lazy, Suspense } from "react";

// Lazy-load so if BugReportDialog or its deps (lucide) fail to import,
// the error page still renders with Try Again. Dialog only loads when user clicks Report Bug.
const BugReportDialog = lazy(() =>
    import("@/components/bug-report/BugReportDialog").then((m) => ({ default: m.BugReportDialog }))
);

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    const [showReport, setShowReport] = useState(false);

    return (
        <html lang="en">
            <head>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
            </head>
            <body style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f9fafb",
                padding: "16px",
                margin: 0,
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}>
                <div style={{ maxWidth: "28rem", width: "100%", textAlign: "center" }}>
                    <div style={{
                        margin: "0 auto 24px",
                        height: "64px",
                        width: "64px",
                        borderRadius: "50%",
                        backgroundColor: "#fee2e2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <svg style={{ height: "32px", width: "32px", color: "#ef4444" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>

                    <h1 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>
                        Something went wrong
                    </h1>
                    <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0 0 24px" }}>
                        Something didn&apos;t work as expected. Help us improve by reporting this bug!
                    </p>

                    <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                        <button
                            onClick={reset}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#111827",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                cursor: "pointer",
                            }}
                        >
                            Try Again
                        </button>
                        <button
                            onClick={() => setShowReport(true)}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "white",
                                color: "#374151",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            Report Bug
                            <svg style={{ height: "16px", width: "16px" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="m8 2 1.88 1.88" /><path d="M14.12 3.88 16 2" /><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" /><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" /><path d="M12 20v-9" /><path d="M6.53 9C4.6 8.8 3 7.1 3 5" /><path d="M6 13H2" /><path d="M3 21c0-2.1 1.7-3.9 3.8-4" /><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" /><path d="M22 13h-4" /><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
                            </svg>
                        </button>
                    </div>
                </div>

                {showReport && (
                    <Suspense fallback={null}>
                        <BugReportDialog
                            error={error}
                            isOpen={showReport}
                            onClose={() => setShowReport(false)}
                        />
                    </Suspense>
                )}
            </body>
        </html>
    );
}
